import * as React from 'react';
import { connect } from 'react-redux';
import { getProgressStatusLocalizationKey } from 'utils/localization';
import { isPassed } from 'utils/comparison';
import { localize } from 'core/localization';
import Reviewable from 'components/review/Reviewable';
import { REVIEW_COMMENT_PROPERTIES, REVIEW_COMMENT_TYPES } from 'constants/review';
import { RootAppState } from 'store/types';
import {
  isOverallMasteryScore,
  isShowContentPagesEnabled,
  shouldSubmitAllQuestions
} from 'store/settings/selectors';
import { isAttemptSubmitted } from 'store/course/selectors';
import { SectionsList, SectionsListItem, SectionsButton } from './SectionsList.styled';
import SectionLearningObjective from '../SectionLearningObjective';
import SectionImage from '../SectionImage';
import SectionContent from '../SectionContent';

export const COUNT_OF_DUMMY_ELEMENTS = 3;

export const getDummyElements = (length: any) => {
  let dummyElements: Array<any> = [];
  if (length < COUNT_OF_DUMMY_ELEMENTS) {
    dummyElements = Array.from({ length: COUNT_OF_DUMMY_ELEMENTS - length }, (v, index) => (
      <SectionsListItem key={index + length} isDummy={true} />
    ));
  }
  return dummyElements;
};

type SectionsListComponentProps = {
  sections: Array<any>;
  navigateToQuestion(id: string, question: string, isCourseOnOnePage: boolean): void;
  learningObjective?: string;
  navigateToLearningObjective(id: string, isCourseOnOnePage: boolean): void;
  isCourseOnOnePage?: boolean;
  isSubmitAtOnce?: boolean;
  isCourseSubmitted?: boolean;
  isCourseProgress?: boolean;
  setSectionRefs?(ref: any): void;
  isShowContentPagesEnabled: boolean;
};

export const SectionsListComponent = ({
  sections,
  navigateToQuestion,
  navigateToLearningObjective,
  setSectionRefs,
  isCourseOnOnePage = false,
  isSubmitAtOnce = false,
  isCourseSubmitted = false,
  isCourseProgress = false,
  isShowContentPagesEnabled = false
}: Readonly<SectionsListComponentProps>) => {
  const { length } = sections;
  const dummyElements = getDummyElements(length);

  return (
    <SectionsList>
      {sections.map((section, index) => {
        const questionId = section.questions[0];
        const {
          id,
          imageUrl,
          status,
          learningObjective,
          score,
          title,
          affectsProgress,
          affectProgressQuestionsCountInSection,
          correctlyAffectProgressQuestionsCountInSection,
          attemptedQuestionsCount,
          sectionProgress
        } = section;

        const isSectionPassed = () => {
          if (isSubmitAtOnce) {
            return !!(isCourseSubmitted && isPassed(status));
          }
          return isPassed(status);
        };

        return (
          <SectionsListItem key={index}>
            <Reviewable
              context={{
                type: REVIEW_COMMENT_TYPES.SECTION,
                property: REVIEW_COMMENT_PROPERTIES.TITLE,
                id,
                title
              }}
            >
              <SectionsButton
                isDummy={false}
                ariaLabel={`${localize('[aria label start section]')} ${title} ${localize(
                  getProgressStatusLocalizationKey(status)
                )}`}
                isPassed={isSectionPassed()}
                tabIndex={0}
                onClick={() =>
                  learningObjective && isShowContentPagesEnabled
                    ? navigateToLearningObjective(id, isCourseOnOnePage)
                    : navigateToQuestion(id, questionId, isCourseOnOnePage)
                }
                isOverallMasteryScore={isCourseProgress}
                ref={setSectionRefs}
              >
                <SectionImage imageUrl={imageUrl} />
                <SectionContent
                  progress={score}
                  sectionProgress={sectionProgress}
                  title={title}
                  status={status}
                  affectsProgress={affectsProgress}
                  affectProgressQuestionsCountInSection={affectProgressQuestionsCountInSection}
                  attemptedQuestionsCount={attemptedQuestionsCount}
                  correctlyAffectProgressQuestionsCountInSection={
                    correctlyAffectProgressQuestionsCountInSection
                  }
                />
              </SectionsButton>
              {learningObjective && isShowContentPagesEnabled && (
                <SectionLearningObjective learningObjective={learningObjective} />
              )}
            </Reviewable>
          </SectionsListItem>
        );
      })}
      {dummyElements.length !== 0 && dummyElements.map(dummy => dummy)}
    </SectionsList>
  );
};
function mapStateToProps(state: RootAppState) {
  return {
    isSubmitAtOnce: shouldSubmitAllQuestions(state),
    isCourseSubmitted: isAttemptSubmitted(state),
    isCourseProgress: isOverallMasteryScore(state),
    isShowContentPagesEnabled: isShowContentPagesEnabled(state)
  };
}

export default connect(mapStateToProps)(SectionsListComponent);
